import { TFunction } from "i18next";

export interface TraitChild {
  id: string;
  name: string;
  desc: string;
  max: number;
}

export interface TraitParent {
  id: string;
  name: string;
  desc: string;
}

export interface TraitGroup {
  parent: TraitParent;
  children: TraitChild[];
}

/**
 * Get trait groups configuration with translations.
 * @param t - Translation function from useTranslation hook
 */
export const getTraitGroups = (t: TFunction): TraitGroup[] => [
  {
    parent: {
      id: "1",
      name: t("paygap.gradeEstimator.parent1"),
      desc: "§ 110 odst. 3 ZP",
    },
    children: [
      {
        id: "1.1",
        name: t("paygap.gradeEstimator.child.1.1.label"),
        desc: t("paygap.gradeEstimator.child.1.1.desc"),
        max: 8,
      },
      {
        id: "1.2",
        name: t("paygap.gradeEstimator.child.1.2.label"),
        desc: t("paygap.gradeEstimator.child.1.2.desc"),
        max: 14,
      },
      {
        id: "1.3",
        name: t("paygap.gradeEstimator.child.1.3.label"),
        desc: t("paygap.gradeEstimator.child.1.3.desc"),
        max: 8,
      },
      {
        id: "1.4",
        name: t("paygap.gradeEstimator.child.1.4.label"),
        desc: t("paygap.gradeEstimator.child.1.4.desc"),
        max: 5,
      },
      {
        id: "1.5",
        name: t("paygap.gradeEstimator.child.1.5.label"),
        desc: t("paygap.gradeEstimator.child.1.5.desc"),
        max: 5,
      },
    ],
  },
  {
    parent: {
      id: "2",
      name: t("paygap.gradeEstimator.parent2"),
      desc: "§ 110 odst. 3 ZP",
    },
    children: [
      {
        id: "2.1",
        name: t("paygap.gradeEstimator.child.2.1.label"),
        desc: t("paygap.gradeEstimator.child.2.1.desc"),
        max: 12,
      },
      {
        id: "2.2",
        name: t("paygap.gradeEstimator.child.2.2.label"),
        desc: t("paygap.gradeEstimator.child.2.2.desc"),
        max: 13,
      },
    ],
  },
  {
    parent: {
      id: "3",
      name: t("paygap.gradeEstimator.parent3"),
      desc: "§ 110 odst. 3 ZP",
    },
    children: [
      {
        id: "3.1",
        name: t("paygap.gradeEstimator.child.3.1.label"),
        desc: t("paygap.gradeEstimator.child.3.1.desc"),
        max: 8,
      },
      {
        id: "3.2",
        name: t("paygap.gradeEstimator.child.3.2.label"),
        desc: t("paygap.gradeEstimator.child.3.2.desc"),
        max: 8,
      },
      {
        id: "3.3",
        name: t("paygap.gradeEstimator.child.3.3.label"),
        desc: t("paygap.gradeEstimator.child.3.3.desc"),
        max: 9,
      },
    ],
  },
  {
    parent: {
      id: "4",
      name: t("paygap.gradeEstimator.parent4"),
      desc: "§ 110 odst. 4 ZP",
    },
    children: [
      {
        id: "4.1",
        name: t("paygap.gradeEstimator.child.4.1.label"),
        desc: t("paygap.gradeEstimator.child.4.1.desc"),
        max: 5,
      },
      {
        id: "4.2",
        name: t("paygap.gradeEstimator.child.4.2.label"),
        desc: t("paygap.gradeEstimator.child.4.2.desc"),
        max: 5,
      },
    ],
  },
];

/**
 * Calculate initial trait max values from trait groups
 */
export const getInitialTraitMaxValues = (
  traitGroups: TraitGroup[],
): Record<string, number> => {
  const initial: Record<string, number> = {};

  traitGroups.forEach(({ parent, children }) => {
    children.forEach((child) => {
      initial[child.id] = child.max || 0;
    });
    initial[parent.id] = children.reduce((sum, child) => sum + (child.max || 0), 0);
  });

  return initial;
};
